import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sparkles,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  Save,
  X,
  ExternalLink,
  Wand2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryOptions = [
  { value: 'industry-tips', label: 'Industry Tips' },
  { value: 'local-news', label: 'Local News' },
];

const emptyPost = {
  id: null,
  title: '',
  excerpt: '',
  content: '',
  category: 'industry-tips',
  cover_image: '',
  tags: [],
  published: false,
};

export const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // the post being edited, or null
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState('');

  // AI generation modal
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiCategory, setAiCategory] = useState('industry-tips');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog/posts`);
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error loading blog posts:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const startNew = () => {
    setEditing({ ...emptyPost });
    setTagsInput('');
  };

  const startEdit = (post) => {
    setEditing({ ...post, cover_image: post.cover_image || '', tags: post.tags || [] });
    setTagsInput((post.tags || []).join(', '));
  };

  const cancelEdit = () => {
    setEditing(null);
    setTagsInput('');
  };

  const savePost = async () => {
    if (!editing.title.trim() || !editing.excerpt.trim() || !editing.content.trim()) {
      alert('Title, excerpt, and content are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        excerpt: editing.excerpt.trim(),
        content: editing.content,
        category: editing.category,
        cover_image: editing.cover_image.trim() || null,
        tags: tagsInput
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        published: editing.published,
      };

      if (editing.id) {
        const res = await axios.put(`${API}/admin/blog/posts/${editing.id}`, payload);
        if (res.data.success) {
          await fetchPosts();
          setEditing(null);
          alert('Post updated!');
        } else {
          alert(res.data.message || 'Failed to update');
        }
      } else {
        const res = await axios.post(`${API}/admin/blog/posts`, payload);
        if (res.data.success) {
          await fetchPosts();
          setEditing(null);
          alert('Post created!');
        } else {
          alert(res.data.message || 'Failed to create');
        }
      }
    } catch (e) {
      console.error('Save error:', e);
      alert('Error saving post.');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      const res = await axios.put(`${API}/admin/blog/posts/${post.id}`, {
        published: !post.published,
      });
      if (res.data.success) await fetchPosts();
    } catch (e) {
      console.error('Toggle publish error:', e);
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/admin/blog/posts/${post.id}`);
      if (res.data.success) await fetchPosts();
    } catch (e) {
      console.error('Delete error:', e);
    }
  };

  const runAiDraft = async () => {
    if (!aiTopic.trim()) {
      alert('Please enter a topic.');
      return;
    }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/admin/blog/generate`, {
        topic: aiTopic.trim(),
        category: aiCategory,
      });
      if (res.data.success && res.data.draft) {
        const d = res.data.draft;
        setEditing({
          ...emptyPost,
          title: d.title || '',
          excerpt: d.excerpt || '',
          content: d.content || '',
          category: d.category || aiCategory,
          tags: d.tags || [],
          published: false,
        });
        setTagsInput((d.tags || []).join(', '));
        setAiOpen(false);
        setAiTopic('');
      } else {
        alert(res.data.message || 'AI generation failed. Try a different topic.');
      }
    } catch (e) {
      console.error('AI error:', e);
      alert('AI generation error. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // --- Editor view ---
  if (editing) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-slate-900">
              {editing.id ? 'Edit Article' : 'New Article'}
            </h2>
            <Button variant="ghost" onClick={cancelEdit} data-testid="blog-cancel-edit">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="e.g. How much does same-day delivery cost in Reading?"
                data-testid="blog-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={editing.category}
                onValueChange={(v) => setEditing({ ...editing, category: v })}
              >
                <SelectTrigger data-testid="blog-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Excerpt / Meta Description * (150–180 chars ideal)</Label>
            <Textarea
              value={editing.excerpt}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              rows={2}
              placeholder="Short summary shown in listing cards and search results."
              data-testid="blog-excerpt-input"
            />
            <p className="text-xs text-slate-500">{editing.excerpt.length} characters</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cover Image URL (optional)</Label>
              <Input
                value={editing.cover_image}
                onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                placeholder="https://..."
                data-testid="blog-cover-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="same day delivery, reading, logistics"
                data-testid="blog-tags-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Article Content (HTML) *</Label>
            <Textarea
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              rows={16}
              className="font-mono text-sm"
              placeholder="<h2>Introduction</h2><p>...</p>"
              data-testid="blog-content-input"
            />
            <p className="text-xs text-slate-500">
              Use HTML tags: <code>&lt;h2&gt;</code>, <code>&lt;h3&gt;</code>,{' '}
              <code>&lt;p&gt;</code>, <code>&lt;ul&gt;</code>, <code>&lt;li&gt;</code>,{' '}
              <code>&lt;strong&gt;</code>, <code>&lt;a href=""&gt;</code>
            </p>
          </div>

          {/* Live preview */}
          {editing.content && (
            <div className="space-y-2">
              <Label className="text-emerald-700">Live Preview</Label>
              <div
                className="border-2 border-dashed border-emerald-200 rounded-lg p-4 bg-emerald-50/30 max-h-96 overflow-y-auto blog-article-content"
                dangerouslySetInnerHTML={{ __html: editing.content }}
              />
              <style>{`
                .blog-article-content h2 { font-size: 1.5rem; font-weight: 700; color: #0f172a; margin-top: 1.5rem; margin-bottom: 0.5rem; }
                .blog-article-content h3 { font-size: 1.2rem; font-weight: 600; color: #0f172a; margin-top: 1rem; margin-bottom: 0.4rem; }
                .blog-article-content p { margin-bottom: 0.8rem; line-height: 1.7; }
                .blog-article-content ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 0.8rem; }
                .blog-article-content a { color: #047857; text-decoration: underline; }
              `}</style>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editing.published}
                onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                className="h-4 w-4"
                data-testid="blog-published-toggle"
              />
              <span className="font-medium text-slate-700">Publish immediately</span>
            </label>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={savePost}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="blog-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" /> Save Article
                </>
              )}
            </Button>
            <Button variant="outline" onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- List view ---
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-slate-900">Blog Articles</h2>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setAiOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            data-testid="blog-ai-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Generate with AI
          </Button>
          <Button onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="blog-new-btn">
            <Plus className="h-4 w-4 mr-2" /> New Article
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-slate-500">
            <p>No blog articles yet. Click "Generate with AI" or "New Article" to start.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`blog-row-${p.slug}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.published
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {p.published ? 'Published' : 'Draft'}
                    </span>
                    <span className="text-xs text-slate-500">
                      {p.category === 'local-news' ? 'Local News' : 'Industry Tips'}
                    </span>
                    <span className="text-xs text-slate-400">
                      {p.published_at || p.updated_at || p.created_at}
                    </span>
                  </div>
                  <h4 className="font-semibold text-slate-900 truncate">{p.title}</h4>
                  <p className="text-sm text-slate-600 line-clamp-1">{p.excerpt}</p>
                  <p className="text-xs text-slate-400 mt-1">/blog/{p.slug}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {p.published && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`/blog/${p.slug}`, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => togglePublish(p)}
                    data-testid={`blog-toggle-${p.slug}`}
                  >
                    {p.published ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(p)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deletePost(p)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* AI generation dialog */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !aiLoading && setAiOpen(false)}>
          <Card className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-purple-600" />
                <h3 className="text-xl font-bold text-slate-900">Generate Article with AI</h3>
              </div>
              <p className="text-sm text-slate-600">
                Give GPT a topic and category. It'll draft a full SEO-optimized article (title,
                excerpt, content, tags) that you can edit before publishing.
              </p>

              <div className="space-y-2">
                <Label>Article topic *</Label>
                <Textarea
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  rows={3}
                  placeholder="e.g. How much does same-day delivery cost in Reading in 2026?"
                  data-testid="ai-topic-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={aiCategory} onValueChange={setAiCategory}>
                  <SelectTrigger data-testid="ai-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={runAiDraft}
                  disabled={aiLoading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  data-testid="ai-generate-btn"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Writing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" /> Generate Draft
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setAiOpen(false)} disabled={aiLoading}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
